import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import {
  AlertTriangle,
  CheckCircle2,
  Info,
  Megaphone,
  Package,
  RefreshCw,
  UserRound,
  Users,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { USAGE_WARNING_THRESHOLD } from "@/lib/constants/plans";
import type { Subscription, UsageStats } from "@/hooks/useBillingAndUsage";

interface UsageLimitsCardProps {
  subscription: Subscription;
  usage: UsageStats;
}

interface UsageMetric {
  key: string;
  label: string;
  icon: LucideIcon;
  used: number;
  limit: number;
  /** Metered per calendar month rather than as a running total */
  isMonthly?: boolean;
}

const getPercentage = (used: number, limit: number): number =>
  limit > 0 ? Math.round((used / limit) * 100) : 0;

const getChipClasses = (percentage: number): string => {
  if (percentage >= 100) return "bg-red-100 text-red-700";
  if (percentage >= USAGE_WARNING_THRESHOLD) return "bg-amber-100 text-amber-700";
  return "bg-gray-100 text-gray-600";
};

const UsageMetricRow: React.FC<{ metric: UsageMetric }> = ({ metric }) => {
  // Negative limit = unlimited on this plan (matches DB enforcement semantics)
  const isUnlimited = metric.limit < 0;
  const percentage = isUnlimited ? 0 : getPercentage(metric.used, metric.limit);
  const Icon = metric.icon;

  return (
    <div>
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-purple-50">
            <Icon className="h-5 w-5 text-purple-600" />
          </div>
          <div>
            <p className="font-semibold text-gray-900">{metric.label}</p>
            <p className="text-sm text-gray-500">
              {isUnlimited
                ? "Unlimited on your plan"
                : `${percentage}% of ${metric.isMonthly ? "monthly limit" : "limit"} used`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <p className="text-sm">
            <span className="font-semibold text-gray-900">
              {metric.used.toLocaleString("en-IN")}
            </span>
            {!isUnlimited && (
              <span className="text-gray-400">
                {" "}
                / {metric.limit.toLocaleString("en-IN")}
              </span>
            )}
          </p>
          <span
            className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
              isUnlimited ? "bg-purple-100 text-purple-700" : getChipClasses(percentage)
            }`}
          >
            {isUnlimited ? "Unlimited" : `${percentage}%`}
          </span>
        </div>
      </div>
      {!isUnlimited && (
        <Progress
          value={Math.min(percentage, 100)}
          className="mt-2 h-2 bg-purple-100 [&>div]:bg-purple-600"
        />
      )}
      {percentage >= 100 ? (
        <p className="mt-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-red-600">
          <AlertTriangle className="h-3.5 w-3.5" />
          Limit exceeded
        </p>
      ) : percentage >= USAGE_WARNING_THRESHOLD ? (
        <p className="mt-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-amber-600">
          <AlertTriangle className="h-3.5 w-3.5" />
          Approaching limit
        </p>
      ) : null}
    </div>
  );
};

export const UsageLimitsCard: React.FC<UsageLimitsCardProps> = ({
  subscription,
  usage,
}) => {
  const metrics: UsageMetric[] = [
    {
      key: "influencers",
      label: "Influencers",
      icon: Users,
      used: usage.influencer_count ?? 0,
      limit: subscription.influencer_limit,
    },
    {
      key: "profile-refreshes",
      label: "Profile Refreshes",
      icon: RefreshCw,
      used: usage.profile_refresh_count ?? 0,
      limit: subscription.profile_refresh_limit,
      isMonthly: true,
    },
    {
      key: "campaigns",
      label: "Campaigns",
      icon: Megaphone,
      used: usage.campaign_count ?? 0,
      limit: subscription.campaign_limit,
    },
    {
      key: "deliverables",
      label: "Deliverables",
      icon: Package,
      used: usage.deliverable_count ?? 0,
      limit: subscription.deliverable_limit,
      isMonthly: true,
    },
    {
      key: "team-members",
      label: "Team Members",
      icon: UserRound,
      used: usage.team_member_count ?? 0,
      limit: subscription.team_member_limit,
    },
  ];

  const warnings = metrics
    .map((metric) => ({
      metric,
      percentage: getPercentage(metric.used, metric.limit),
    }))
    .filter(({ percentage }) => percentage >= USAGE_WARNING_THRESHOLD);

  return (
    <Card>
      <CardHeader className="pb-4">
        <CardTitle className="text-xl">Usage &amp; Limits</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-5">
          {metrics.map((metric) => (
            <UsageMetricRow key={metric.key} metric={metric} />
          ))}
        </div>

        <div className="mt-6 rounded-xl bg-purple-50 p-4">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-purple-600">
              <Info className="h-4 w-4 text-white" />
            </div>
            <h4 className="font-semibold text-purple-700">Usage Information</h4>
          </div>
          <ul className="mt-3 space-y-2 pl-11 text-sm">
            {warnings.length > 0 ? (
              warnings.map(({ metric, percentage }) => (
                <li
                  key={metric.key}
                  className={`flex items-center gap-2 font-medium ${
                    percentage >= 100 ? "text-red-600" : "text-amber-600"
                  }`}
                >
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  {percentage >= 100
                    ? `You've exceeded your ${metric.label.toLowerCase()} limit`
                    : `You're approaching your ${metric.label.toLowerCase()} limit`}
                </li>
              ))
            ) : (
              <li className="flex items-center gap-2 font-medium text-green-700">
                <CheckCircle2 className="h-4 w-4 shrink-0" />
                You're well within your usage limits
              </li>
            )}
          </ul>
        </div>
      </CardContent>
    </Card>
  );
};
